import React from 'react';
import { ui } from '../../../config/uiSizes';

export type ModalVariant =
  | 'delete'
  | 'view'
  | 'startMigration'
  | 'attention'
  | 'runMigration'
  | 'runSnapshot'
  | 'transformationViewDetails'
  | 'preMigrationConfirm'
  | 'repliCloudFAQs'
  | 'rollbackPreview';

interface ModalProps {
  isOpen: boolean;
  variant: ModalVariant;
  title: React.ReactNode;
  onClose: () => void;
  children?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  okLabel?: string;
  onConfirm?: () => void;
  onSubmit?: () => void;
  showConfirmButton?: boolean;
  showOkButton?: boolean;
  showIcon?: boolean;
  confirmLoading?: boolean;
  confirmLoadingLabel?: string;
  confirmDisabled?: boolean;
  customButtons?: React.ReactNode;
  showCloseButton?: boolean;
  setError?: React.Dispatch<React.SetStateAction<string | null>>;
  error?: string | null;
}

const DeleteIcon = () => (
  <svg className={`${ui.svg.sm}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ViewIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={`${ui.svg.sm}`}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0118 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.75m-8.25-3l1.5 1.5 3-3.75"
    />
  </svg>
);

const AttentionIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={`${ui.svg.sm}`}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0118 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.75m-8.25-3l1.5 1.5 3-3.75"
    />
  </svg>
);


const StartMigrationIcon = () => (
  <svg className={`${ui.svg.sm}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
    />
  </svg>
);

const RollbackIcon = () => (
  <svg className={`${ui.svg.sm}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
  </svg>
);

const RunMigrationIcon = () => (
  <svg
    role="img"
    aria-labelledby="confirmTitle"
    viewBox="0 0 24 24"
    className="w-12 h-12 text-green-500"
  >
    <title id="confirmTitle">Confirm</title>
    <circle cx="12" cy="12" r="10" fill="currentColor" />
    <path
      fill="#fff"
      d="M10.2 13.8l-2.2-2.2a.75.75 0 0 0-1.06 1.06l2.75 2.75c.3.3.77.3 1.06 0l5.25-5.25a.75.75 0 1 0-1.06-1.06L10.2 13.8z"
    />
  </svg>
);

const TransformationViewDetailsIcon = () => (
  <svg
    role="img"
    aria-labelledby="transformationDetailsTitle"
    viewBox="0 0 24 24"
    className="w-12 h-12 text-blue-500"
  >
    <title id="transformationDetailsTitle">Transformation Details</title>
    <circle cx="12" cy="12" r="10" fill="currentColor" />
    <path
      fill="#fff"
      d="M8 7a1 1 0 011-1h6a1 1 0 011 1v1h-8V7zm-1 3h10v7a1 1 0 01-1 1H8a1 1 0 01-1-1v-7zm3 2a.5.5 0 000 1h4a.5.5 0 000-1h-4zm0 2a.5.5 0 000 1h4a.5.5 0 000-1h-4zm0 2a.5.5 0 000 1h2a.5.5 0 000-1h-2z"
    />
  </svg>
);

const VARIANT_STYLES = {
  delete: {
    iconBg: 'bg-red-100 dark:bg-red-700',
    iconColor: 'text-red-600 dark:text-red-200',
    confirmBg: 'bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600',
    modalBg: 'bg-white dark:bg-[#0f1720]',
    modalBorder: 'border-red-300 dark:border-red-500',
    Icon: DeleteIcon
  },
  view: {
    iconBg: 'bg-blue-100 dark:bg-blue-900',
    iconColor: 'text-blue-600 dark:text-blue-400',
    confirmBg: 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600',
    modalBg: 'bg-white dark:bg-[#0f1720]',
    modalBorder: 'border-gray-300 dark:border-gray-700',
    Icon: ViewIcon
  },
  attention: {
    iconBg: 'bg-yellow-100 dark:bg-yellow-900',
    iconColor: 'text-yellow-600 dark:text-yellow-400',
    confirmBg: 'bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-700 dark:hover:bg-yellow-600',
    modalBg: 'bg-white dark:bg-[#0f1720]',
    modalBorder: 'border-gray-300 dark:border-gray-700',
    Icon: AttentionIcon
  },
  startMigration: {
    iconBg: 'bg-green-100 dark:bg-green-900',
    iconColor: 'text-green-600 dark:text-green-400',
    confirmBg: 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600',
    modalBg: 'bg-white dark:bg-[#0f1720]',
    modalBorder: 'border-gray-300 dark:border-gray-700',
    Icon: StartMigrationIcon
  },
  runMigration: {
    iconBg: 'bg-transparent',
    iconColor: 'text-white',
    confirmBg: 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600',
    modalBg: 'bg-white dark:bg-[#0f1720]',
    modalBorder: 'border-gray-200 dark:border-gray-800/60',
    Icon: RunMigrationIcon
  },
  runSnapshot: {
    iconBg: 'bg-transparent',
    iconColor: 'text-white',
    confirmBg: 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600',
    modalBg: 'bg-white dark:bg-[#0f1720]',
    modalBorder: 'border-gray-200 dark:border-gray-800/60',
    Icon: RunMigrationIcon
  },
  transformationViewDetails: {
    iconBg: 'bg-transparent',
    iconColor: 'text-white',
    confirmBg: 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600',
    modalBg: 'bg-white dark:bg-[#0f1720]',
    modalBorder: 'border-gray-200 dark:border-gray-800/60',
    Icon: TransformationViewDetailsIcon
  },
  preMigrationConfirm: {
    iconBg: 'bg-transparent',
    iconColor: 'text-white',
    confirmBg: 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600',
    modalBg: 'bg-white dark:bg-[#0f1720]',
    modalBorder: 'border-gray-200 dark:border-gray-800/60',
    Icon: RunMigrationIcon
  },
  repliCloudFAQs: {
    iconBg: 'bg-transparent',
    iconColor: 'text-white',
    confirmBg: 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600',
    modalBg: 'bg-white dark:bg-[#0f1720]',
    modalBorder: 'border-gray-200 dark:border-gray-700',
    Icon: ViewIcon
  },
  rollbackPreview: {
    iconBg: 'bg-blue-100 dark:bg-blue-900/40',
    iconColor: 'text-blue-600 dark:text-blue-400',
    confirmBg: 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600',
    modalBg: 'bg-white dark:bg-[#0f1720]',
    modalBorder: 'border-gray-200 dark:border-gray-700',
    Icon: RollbackIcon
  }
} as const;
export const Modal: React.FC<ModalProps> = React.memo(
  ({
    isOpen,
    variant,
    title,
    onClose,
    onSubmit,
    children,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    okLabel = 'OK',
    onConfirm,
    showConfirmButton = true,
    showIcon = true,
    showOkButton = false,
    confirmLoading = false,
    confirmLoadingLabel,
    confirmDisabled = false,
    customButtons,
    showCloseButton = false,
    setError,
    error
  }) => {
    const styles = VARIANT_STYLES[variant];
    const IconComponent = styles.Icon;

    // Keep latest callbacks in refs so effects never need them as deps.
    const onCloseRef = React.useRef(onClose);
    const setErrorRef = React.useRef(setError);
    React.useEffect(() => { onCloseRef.current = onClose; });
    React.useEffect(() => { setErrorRef.current = setError; });

    const handleClose = React.useCallback(() => {
      setErrorRef.current?.(null);
      onCloseRef.current();
    }, []);

    const handleBackdropClick = React.useCallback(
      (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
          setErrorRef.current?.(null);
          onCloseRef.current();
        }
      },
      []
    );

    React.useEffect(() => {
      if (!isOpen) return;

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setErrorRef.current?.(null);
          onCloseRef.current();
        }
      };

      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen]);

    // Prevent background scrolling when modal is open
    React.useEffect(() => {
      if (!isOpen) return;
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }, [isOpen]);

    // Early return AFTER all hooks
    if (!isOpen) return null;

    return (
      <>
        {/* Backdrop with blur */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-fade-in-backdrop"
          onClick={handleBackdropClick}
          aria-hidden="true"
        />

        {/* Modal */}
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div
            className={`${styles.modalBg} rounded-lg shadow-2xl ${ui.widthAndHeight.modal} overflow-y-auto border-2 ${styles.modalBorder} animate-fade-in relative`}
          >
            {/* Optional X button at top right */}
            {showCloseButton && (
              <button
                type="button"
                aria-label="Close modal"
                onClick={handleClose}
                className={`absolute top-4 right-4 z-10 ${ui.page.modalCloseButton} rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                <svg className={`${ui.svg.sm}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <div className={`${ui.size.modalView}`}>
              {/* Icon */}
              {showIcon && (
                <div
                  className={`flex items-center justify-center ${ui.widthAndHeight.base} mx-auto ${styles.iconBg} rounded-full mb-4`}
                >
                  <div className={styles.iconColor}>
                    <IconComponent />
                  </div>
                </div>
              )}

              {/* Title (neutral, reusable styling) */}
              <h1
                id="modal-title"
                className={`${ui.text.modalTitle} font-extrabold text-gray-900 dark:text-white text-center mb-2 tracking-tight`}
              >
                {title}
              </h1>

              {/* Small decorative divider (subtle, neutral) */}
              <div className="h-[2px] w-16 mx-auto rounded bg-gray-200 dark:bg-gray-700 mb-4" />

              {/* Content */}
              {children && <div className="mb-6">{children}</div>}

              {/* Error message */}
              {error && (
                <div className="mb-4 px-2 text-center text-red-700 font-semibold bg-red-50 rounded py-2 dark:bg-red-700 dark:text-red-100 max-h-40 overflow-auto">
                  {error}
                </div>
              )}

              {/* Buttons */}
              {customButtons ? (
                <div className="flex gap-3">{customButtons}</div>
              ) : (
                <div className="flex gap-3">
                  <button
                    onClick={handleClose}
                    className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-gray-700 dark:text-gray-300 font-semibold shadow transition hover:bg-gray-50 dark:hover:bg-gray-600"
                    type="button"
                  >
                    {cancelLabel}
                  </button>

                  {showOkButton && onSubmit && (

                    <button
                      onClick={onSubmit}
                      className="flex-1 rounded-lg bg-green-600 px-4 py-2 text-white font-semibold shadow transition hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600"
                      type="button"
                    >
                      {okLabel}
                    </button>
                  )}

                  {showConfirmButton && onConfirm && (
                    <button
                      onClick={onConfirm}
                      disabled={confirmLoading || confirmDisabled}
                      aria-disabled={confirmLoading || confirmDisabled}
                      className={`flex-1 rounded-lg ${styles.confirmBg} px-4 py-2 text-white font-semibold shadow transition ${confirmLoading || confirmDisabled ? 'opacity-60 cursor-not-allowed' : ''} flex items-center justify-center gap-2`}
                      type="button"
                    >
                      {confirmLoading && (
                        <svg
                          className="animate-spin h-5 w-5 text-white"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          ></path>
                        </svg>
                      )}
                      <span>
                        {confirmLoading ? (confirmLoadingLabel ?? confirmLabel) : confirmLabel}
                      </span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
);

Modal.displayName = 'Modal';
